/**
 * Cliente del historial de liquidaciones ya migrado a psiplatform-be
 * (GET /psychologist/liquidaciones), reemplazo gradual del webhook de Make. Ver
 * MetricsLiquidacionTab.tsx (mergeLiquidacionPeriodos) para cómo se combina con la
 * fuente vieja.
 */

import { beHttp } from './beHttp';

export interface LiquidacionBeSesion {
  fecha: string;
  pacienteNombre: string;
}

export interface LiquidacionBeServicio {
  tipoServicio: string;
  cantidadSesiones: number;
  precioPorSesion: number;
  subtotal: number;
  bonificacion: number;
  canceladasYaLiquidadas: number;
  sesiones: LiquidacionBeSesion[];
}

/** Bono incluido en `totalACobrar`/`montoUsd` del período (hoy solo PSI+ trimestral). */
export interface LiquidacionBeBono {
  tipo: 'PSI_PLUS';
  quarterYear: number;
  /** 1 a 4 — trimestre calendario. */
  quarter: number;
  tier: 'bronze' | 'silver' | 'gold';
  montoUsd: number;
  montoEnMonedaDePago: number;
}

export interface LiquidacionBePeriodo {
  /** Label "Julio 26" — mismo formato que el campo `Periodo` del webhook de Make. */
  periodo: string;
  totalACobrar: number;
  moneda: string;
  tipoCambio: string | null;
  montoUsd: number;
  servicios: LiquidacionBeServicio[];
  /** Opcional por compatibilidad con un backend anterior al campo: ausente = sin bonos.
   *  `totalACobrar` YA incluye estos montos — no es la suma de `servicios`. */
  bonos?: LiquidacionBeBono[];
}

export interface LiquidacionesBeResponse {
  periodos: LiquidacionBePeriodo[];
}

export function fetchLiquidacionesBe(): Promise<LiquidacionesBeResponse> {
  return beHttp.get<LiquidacionesBeResponse>(
    '/api/psychologist/liquidaciones',
    undefined,
    { showErrorToast: false },
  );
}

/** Pago anticipado ya informado por finanzas-gitlab para un período del esquema range —
 *  ver RangeEarlyPaymentRecord en psiplatform-be/prisma/supabase/schema.prisma. */
export interface RangeEarlyPaymentBe {
  periodYear: number;
  periodMonth: number;
  /** Total acumulado del período, no delta. */
  sessionsPaid: number;
  amountPaid: number;
  paidAt: string;
}

/** Solicitud de anticipada de la propia psicóloga — ver AnticipadaRequest en
 *  psiplatform-be/prisma/supabase/schema.prisma. */
export interface OwnAnticipadaRequestBe {
  requestId: string;
  periodYear: number;
  periodMonth: number;
  /** ISO 8601. */
  requestedAt: string;
  requestedSessions: number;
  estimatedAmount: number;
  /** ISO 8601 si finanzas-gitlab la descartó ("No corresponde"). Opcional por compatibilidad con
   *  un backend anterior al campo: ausente = no descartada. */
  dismissedAt?: string | null;
  /** Motivo escrito por finanzas-gitlab; la psicóloga lo ve tal cual. */
  dismissalReason?: string | null;
}

export interface RangeEarlyPaymentsBeResponse {
  pagos: RangeEarlyPaymentBe[];
  /** Más nueva primero. Opcional por compatibilidad con un backend anterior al campo:
   *  ausente = sin solicitudes registradas. */
  solicitudes?: OwnAnticipadaRequestBe[];
}

/** GET /psychologist/liquidaciones/anticipada — consumido por useNZSettlementSchedule. */
export function fetchRangeEarlyPaymentsBe(): Promise<RangeEarlyPaymentsBeResponse> {
  return beHttp.get<RangeEarlyPaymentsBeResponse>(
    '/api/psychologist/liquidaciones/anticipada',
    undefined,
    { showErrorToast: false },
  );
}

/** Body de POST /psychologist/liquidaciones/anticipada/solicitar — mismos números que ya
 *  calculó `calculateNZSettlementSchedule` para el mes en curso (ver
 *  NZAcumulandoZonasView). El endpoint no vuelve a calcular nada: solo dispara el mail a
 *  administración/liquidaciones para que finanzas-gitlab la gestione a mano. */
export interface SolicitarLiquidacionAnticipadaInput {
  /** 'YYYY-MM' */
  month: string;
  sessionsThisMonth: number;
  carriedOverFromPreviousMonths: number;
  totalConsideredForThreshold: number;
  earlyLiquidationAmount: number;
}

export interface SolicitarLiquidacionAnticipadaResponse {
  /** false si la sesión es impersonada, falta el email en el token, falló el envío del
   *  mail, o está en cooldown (ver `reason`) — nunca un error: el mail es un aviso, no
   *  una condición de éxito para la psicóloga (ver
   *  LiquidacionesService.solicitarLiquidacionAnticipada). */
  sent: boolean;
  /** Presente solo cuando `sent` es false por haber pedido lo mismo hace menos de 24hs —
   *  para no mostrarle "no pudimos enviar" cuando en realidad ya se envió recientemente. */
  reason?: 'throttled';
}

export function solicitarLiquidacionAnticipada(
  input: SolicitarLiquidacionAnticipadaInput,
): Promise<SolicitarLiquidacionAnticipadaResponse> {
  return beHttp.post<SolicitarLiquidacionAnticipadaResponse, SolicitarLiquidacionAnticipadaInput>(
    '/api/psychologist/liquidaciones/anticipada/solicitar',
    input,
    { showErrorToast: false },
  );
}

/** Sesión que integró un pago de tramo congelado — mismo shape que
 *  LiquidacionesService.congelarRangePayment persiste, tal cual lo informó finanzas-gitlab. */
export interface RangePeriodPaymentSesionBe {
  sourcePeriodId: string;
  sessionId: string;
  acuityId: string | null;
  status: string;
  sessionDate: string;
  processType: string | null;
  patientNames: string[];
}

/** Pago de tramo del esquema range/NZ ya congelado (verificado contra el recálculo
 *  independiente de psiplatform-be) — ver RangePeriodPayment en schema.prisma. A
 *  diferencia del schedule en vivo (calculateNZSettlementSchedule), esto es un hecho ya
 *  ocurrido: no se recalcula ni se muestra como "estimado". */
export interface RangePeriodPaymentBe {
  /** Label "Julio 26" — solo para mostrar, no usar para matchear contra un mes 'YYYY-MM'
   *  (para eso están periodYear/periodMonth). */
  periodo: string;
  periodYear: number;
  periodMonth: number;
  /** Acumulado BRUTO del período (sin descontar `earlyPaymentSessionsPaid`). */
  sessionsCount: number;
  earlyPaymentSessionsPaid: number;
  tierLabel: string;
  tierAmount: number;
  /** Moneda en la que está expresado `tierAmount` (típicamente "usd") — distinta de
   *  `currency`, la moneda real de cobro de la psicóloga. */
  calcCurrency: string;
  currency: string;
  montoEnMonedaDePago: number;
  exchangeRate: string | null;
  montoUsd: number;
  paidAt: string;
  sesiones: RangePeriodPaymentSesionBe[];
}

export interface RangePeriodPaymentsBeResponse {
  periodos: RangePeriodPaymentBe[];
}

/** GET /psychologist/liquidaciones/range — consumido por useNZSettlementSchedule. */
export function fetchRangePeriodPaymentsBe(): Promise<RangePeriodPaymentsBeResponse> {
  return beHttp.get<RangePeriodPaymentsBeResponse>(
    '/api/psychologist/liquidaciones/range',
    undefined,
    { showErrorToast: false },
  );
}
